import axios from 'axios';
import localforage from 'localforage';
import moment from 'moment';
import { safeSetItem } from './storageQuotaGuard';
import { withTimeout } from './withTimeout';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;
const LAST_CACHE_DATE_KEY = 'lastImageCacheDate';
const PENDING_IMAGE_CACHE_KEY = 'pendingImageCacheProducts';

const NOT_FOUND_TTL_MS = 3 * 24 * 60 * 60 * 1000;
const STORAGE_TIMEOUT_MS = 15000;

const withStorageTimeout = (promise) =>
  withTimeout(promise, STORAGE_TIMEOUT_MS, () => new Error('Lokalna baza uređaja ne odgovara.'));

const throwIfImageRequestAborted = (signal) => {
  if (signal?.aborted) {
    const error = new Error('Image request aborted');
    error.name = 'AbortError';
    throw error;
  }
};

export const CacheMode = {
  SYNC: 'sync',
  REFRESH: 'refresh',
  UPDATE: 'update',
};

// MOBILE: Detect low-memory devices
const isLowMemoryDevice = () => {
  const memory = navigator.deviceMemory;
  const width = window.innerWidth;
  return (memory && memory <= 2) || width < 768; // 2GB or mobile - fixed mixed operators
};

// MOBILE: Adaptive cache sizes based on device memory
const getCacheConfig = () => {
  const isLowMemory = isLowMemoryDevice();
  return {
    maxImages: isLowMemory ? 50 : 200, // Limit cached images
    maxImageSize: isLowMemory ? 500 : 2000, // KB limit per image
    totalSizeLimit: isLowMemory ? 20 : 100, // MB total cache limit
    cleanupThreshold: isLowMemory ? 40 : 80, // MB before cleanup - more reasonable
  };
};

const imageStore = localforage.createInstance({
  name: 'OfflineStoreRegistry',
  storeName: 'strapiImages',
});

const imageUrlMappingStore = localforage.createInstance({
  name: 'OfflineStoreRegistry',
  storeName: 'productCodeToImageUrl',
});

// Store for caching "not found" images to prevent repeated API calls
const notFoundImagesStore = localforage.createInstance({
  name: 'OfflineStoreRegistry',
  storeName: 'notFoundImages',
});

const writeCacheItem = async (store, key, value) => {
  if ((await withStorageTimeout(safeSetItem(store, key, value))) === false) {
    throw new Error('Nije moguće sačuvati podatke slike na uređaju.');
  }
};

const markImageMissing = (productCode) =>
  writeCacheItem(notFoundImagesStore, productCode, {
    status: 'missing',
    checkedAt: Date.now(),
  });

const getProductCode = (product) => {
  const code = product?.code ?? product?.sifra;
  return code == null ? null : String(code).trim() || null;
};

const getProductImageUrl = (product) => {
  const image = product?.slika;
  const imagePath = image?.formats?.small?.url ??
    image?.formats?.thumbnail?.url ?? image?.formats?.medium?.url ?? image?.url;
  if (!imagePath) return null;
  return imagePath.startsWith('http') ? imagePath : `${STRAPI_URL}${imagePath}`;
};

const ensureImageMapping = async (productCode, imageUrl) => {
  if ((await withStorageTimeout(imageUrlMappingStore.getItem(productCode))) !== imageUrl) {
    await writeCacheItem(imageUrlMappingStore, productCode, imageUrl);
  }
};

const getPendingImageProducts = async () => {
  const products = await withStorageTimeout(localforage.getItem(PENDING_IMAGE_CACHE_KEY));
  return Array.isArray(products) ? products.filter(getProductCode) : [];
};

export const getPendingImageCacheCount = async () =>
  (await getPendingImageProducts()).length;

const fetchCachedImageBlob = async (imageUrl, refresh) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(imageUrl, {
      signal: controller.signal,
      ...(refresh ? { cache: 'reload' } : {}),
    });
    if (!response.ok) throw new Error('Preuzimanje slike nije uspjelo: ' + response.status);
    const blob = await response.blob();
    throwIfImageRequestAborted(controller.signal);
    if (!blob.size) throw new Error('Preuzeta slika je prazna.');
    return blob;
  } finally {
    clearTimeout(timeout);
  }
};

// Note: imageCacheStatsStore removed as it was unused

/**
 * Fetches the image URL from Strapi based on the product code.
 *
 * @param {string} productCode - The product code to fetch the image for.
 * @return {Promise<string|null>} - The image URL or null if not found.
 * */
const getImageUrlFromStrapi = async (productCode, { signal } = {}) => {
  throwIfImageRequestAborted(signal);
  if (!STRAPI_URL) {
    throw new Error('STRAPI_URL nije konfigurisan.');
  }
  if (!productCode) {
    return null;
  }
  const apiUrl = `${STRAPI_URL}/products?sifra=${encodeURIComponent(
    productCode,
  )}`;
  // Axios 0.21 uses CancelToken; its AbortSignal support is not available yet.
  const cancellation = signal ? axios.CancelToken.source() : null;
  const onAbort = () => cancellation.cancel('Image request aborted');
  if (signal) signal.addEventListener('abort', onAbort, { once: true });
  try {
    const res = await axios.get(apiUrl, {
      timeout: 5000,
      ...(cancellation ? { cancelToken: cancellation.token } : {}),
    });
    throwIfImageRequestAborted(signal);
    if (!Array.isArray(res?.data)) {
      throw new Error('Neočekivani odgovor servera za slike.');
    }
    return getProductImageUrl(res.data[0]);
  } catch (error) {
    throwIfImageRequestAborted(signal);
    throw error;
  } finally {
    if (signal) signal.removeEventListener('abort', onAbort);
  }
};

/**
 * Fetches image URLs from Strapi for multiple product codes in a single batch request.
 *
 * @param {string[]} productCodes - Array of product codes to fetch images for.
 * @return {Promise<Map<string, string>>} - Map of productCode -> imageUrl. Missing products will not be in the map.
 * */
const getBatchImageUrlsFromStrapi = async (productCodes) => {
  if (!STRAPI_URL) {
    throw new Error('STRAPI_URL nije konfigurisan.');
  }
  if (!productCodes || productCodes.length === 0) {
    return new Map();
  }

  const resultMap = new Map();
  const codesQuery = productCodes.map(code => `sifra=${encodeURIComponent(code)}`).join('&');
  const apiUrl = `${STRAPI_URL}/products?${codesQuery}`;

  const res = await axios.get(apiUrl, { timeout: 10000 });
  if (!Array.isArray(res?.data)) {
    throw new Error('Neočekivani odgovor servera za slike.');
  }
  for (const product of res.data) {
    const code = getProductCode(product);
    const imageUrl = getProductImageUrl(product);
    if (code && imageUrl) resultMap.set(code, imageUrl);
  }

  return resultMap;
};

const getUpdatedProducts = async (sinceDateISO) => {
  if (!STRAPI_URL) {
    console.error('STRAPI_URL is not configured.');
    throw new Error('STRAPI_URL nije konfigurisan.');
  }
  if (!sinceDateISO) {
    console.warn(
      '[Cache Update] No last cache date provided, cannot fetch updated products.',
    );
    return [];
  }

  const formattedDate = moment(sinceDateISO).toISOString();
  const apiUrl = `${STRAPI_URL}/products?updated_at_gte=${formattedDate}&_limit=10000`;

  console.log(
    `[Cache Update] Fetching products updated since: ${formattedDate} from ${apiUrl}`,
  );

  try {
    const res = await axios.get(apiUrl, { timeout: 15000 });
    if (Array.isArray(res.data)) {
      console.log(`[Cache Update] Found ${res.data.length} updated products.`);
      return res.data;
    } else {
      console.error(
        '[Cache Update] Unexpected response format from Strapi:',
        res.data,
      );
      throw new Error(
        'Neočekivani format odgovora od servera za ažurirane proizvode.',
      );
    }
  } catch (error) {
    console.error(`[Cache Update] Error fetching updated products:`, error);
    const message =
      error.response?.data?.message ||
      error.message ||
      'Nepoznata greška servera.';
    throw new Error(`Greška pri preuzimanju ažuriranih proizvoda: ${message}`);
  }
};

export const getStoredImageOrFetch = async (productCode, { signal } = {}) => {
  if (!productCode) return null;

  try {
    throwIfImageRequestAborted(signal);
    let imageUrl = await imageUrlMappingStore.getItem(productCode);
    throwIfImageRequestAborted(signal);

    if (imageUrl) {
      const storedBlob = await imageStore.getItem(imageUrl);
      throwIfImageRequestAborted(signal);
      if (storedBlob instanceof Blob) {
        return URL.createObjectURL(storedBlob);
      }
    }

    const missing = await notFoundImagesStore.getItem(productCode);
    throwIfImageRequestAborted(signal);
    if (
      missing?.status === 'missing' &&
      typeof missing.checkedAt === 'number' &&
      Date.now() - missing.checkedAt < NOT_FOUND_TTL_MS
    ) {
      return null;
    }

    imageUrl = await getImageUrlFromStrapi(productCode, { signal });
    throwIfImageRequestAborted(signal);
    if (!imageUrl) {
      await markImageMissing(productCode);
      return null;
    }

    await safeSetItem(imageUrlMappingStore, productCode, imageUrl);
    throwIfImageRequestAborted(signal);

    const storedBlob = await imageStore.getItem(imageUrl);
    throwIfImageRequestAborted(signal);
    if (storedBlob instanceof Blob) {
      return URL.createObjectURL(storedBlob);
    }

    const networkResponse = await fetch(imageUrl, { signal });
    if (!networkResponse.ok) {
      console.error(
        `[Display Img] Network fetch failed for ${imageUrl} with status: ${networkResponse.status}`,
      );
      return null;
    }

    const imageBlob = await networkResponse.blob();
    throwIfImageRequestAborted(signal);

    await safeSetItem(imageStore, imageUrl, imageBlob);
    throwIfImageRequestAborted(signal);
    return URL.createObjectURL(imageBlob);
  } catch (error) {
    if (signal?.aborted || error.name === 'AbortError') return null;
    console.error(
      `[Display Img] Error getting/fetching image for ${productCode}:`,
      error,
    );
    return null;
  }
};

/**
 * Attempts to retrieve an image blob ONLY from the localforage cache.
 * Makes NO network requests.
 * @param {string | null} productCode - The product code to retrieve the image for.
 * @returns {Promise<string | null>} A Blob URL string if found in cache, otherwise null.
 */
export const getStoredImageOnly = async (productCode, { signal } = {}) => {
  if (!productCode) {
    return null;
  }

  try {
    throwIfImageRequestAborted(signal);
    // Get the image URL from the mapping store instead of API
    const imageUrl = await imageUrlMappingStore.getItem(productCode);
    throwIfImageRequestAborted(signal);
    if (!imageUrl) {
      return null;
    }

    const storedBlob = await imageStore.getItem(imageUrl);
    throwIfImageRequestAborted(signal);

    if (storedBlob instanceof Blob) {
      return URL.createObjectURL(storedBlob);
    } else {
      return null;
    }
  } catch (error) {
    if (signal?.aborted || error.name === 'AbortError') return null;
    console.error(
      `[Cache Only] Error retrieving image blob for ${productCode}:`,
      error,
    );
    return null;
  }
};

/**
 * Stores product images in localForage.
 *
 * @param {Array} products - Array of product objects.
 * @param {Function} onProgress - Callback function to report progress.
 * @param {string} mode - Cache mode (SYNC, REFRESH, UPDATE).
 * @param {string|null} lastUpdateCheckDate - Date for UPDATE mode.
 * @param {boolean} clearNotFoundFirst - Whether to clear not-found cache first.
 * @param {Function} shouldCancel - Optional callback that returns true if operation should be cancelled.
 * @return {Promise<Object>} - Object containing success, failed, skipped counts, and cancelled flag.
 * */
const runProductImageCache = async (
  products,
  onProgress,
  mode = CacheMode.SYNC,
  lastUpdateCheckDate = null,
  clearNotFoundFirst = false,
  shouldCancel = null,
) => {
  const startedAt = new Date().toISOString();
  const pending = new Map((await getPendingImageProducts()).map((product) => [
    getProductCode(product),
    { code: getProductCode(product), refresh: Boolean(product.refresh) },
  ]));
  let pendingChanged = false;
  const rememberRetry = (product) => {
    const previous = pending.get(product.code);
    const refresh = Boolean(product.refresh);
    if (!previous || previous.refresh !== refresh) {
      pending.set(product.code, { code: product.code, refresh });
      pendingChanged = true;
    }
  };
  const resolveRetry = (code) => {
    if (pending.delete(code)) pendingChanged = true;
  };

  if (clearNotFoundFirst) await clearNotFoundCache();

  let sourceProducts;
  let canAdvanceCheckpoint = true;
  if (mode === CacheMode.UPDATE) {
    if (onProgress) onProgress(0, 0, 'fetching_updates');
    const storedDate = await getLastCacheDate();
    const since = storedDate && (!lastUpdateCheckDate || storedDate > lastUpdateCheckDate)
      ? storedDate : lastUpdateCheckDate;
    sourceProducts = since ? await getUpdatedProducts(since) : [];
    canAdvanceCheckpoint = Boolean(since);
  } else if (mode === CacheMode.SYNC || mode === CacheMode.REFRESH) {
    sourceProducts = products || [];
  } else {
    throw new Error('Nevažeći mod keširanja: ' + mode);
  }

  let productsToProcess = sourceProducts;
  let failedCount = 0;
  if (mode === CacheMode.UPDATE) {
    const work = new Map(pending);
    for (const product of sourceProducts) {
      const code = getProductCode(product);
      if (!code) failedCount++;
      else work.set(code, { code, refresh: true });
    }
    productsToProcess = Array.from(work.values());
  }
  const toWorkProduct = (product) => {
    const code = getProductCode(product);
    return { code, refresh: mode === CacheMode.UPDATE
      ? Boolean(product.refresh)
      : mode === CacheMode.REFRESH || Boolean(pending.get(code)?.refresh) };
  };
  const total = productsToProcess.length;
  let successCount = 0;
  let skippedCount = 0;
  let notFoundCount = 0;
  let processedCount = 0;
  let cancelled = false;
  const BATCH_SIZE = 50;

  for (let batchStart = 0; batchStart < total; batchStart += BATCH_SIZE) {
    if (shouldCancel && shouldCancel()) {
      cancelled = true;
      break;
    }
    const batch = productsToProcess.slice(batchStart, batchStart + BATCH_SIZE).map(toWorkProduct);
    const validProducts = batch.filter((product) => product.code);
    let imageUrls;
    try {
      imageUrls = await getBatchImageUrlsFromStrapi(validProducts.map((product) => product.code));
    } catch (error) {
      validProducts.forEach(rememberRetry);
      failedCount += batch.length;
      processedCount += batch.length;
      if (onProgress) onProgress(processedCount, total, 'processing');
      continue;
    }

    for (const product of batch) {
      if (shouldCancel && shouldCancel()) {
        cancelled = true;
        break;
      }
      if (!product.code) {
        failedCount++;
        processedCount++;
        continue;
      }
      const imageUrl = imageUrls.get(product.code);
      let needsRefresh = product.refresh;
      try {
        if (!imageUrl) {
          await markImageMissing(product.code);
          notFoundCount++;
          failedCount++;
        } else {
          const cached = !product.refresh &&
            await withStorageTimeout(imageStore.getItem(imageUrl));
          if (cached instanceof Blob) {
            await ensureImageMapping(product.code, imageUrl);
            skippedCount++;
          } else {
            const blob = await fetchCachedImageBlob(imageUrl, product.refresh);
            await writeCacheItem(imageStore, imageUrl, blob);
            needsRefresh = false;
            await ensureImageMapping(product.code, imageUrl);
            successCount++;
          }
        }
        resolveRetry(product.code);
      } catch (error) {
        rememberRetry({ code: product.code, refresh: needsRefresh });
        failedCount++;
      }
      processedCount++;
      if (onProgress) onProgress(processedCount, total, 'processing');
    }
    if (cancelled) break;
  }

  if (pendingChanged) {
    await writeCacheItem(localforage, PENDING_IMAGE_CACHE_KEY, Array.from(pending.values()));
  }
  if (!cancelled && canAdvanceCheckpoint && (total > 0 || mode === CacheMode.UPDATE)) {
    await writeCacheItem(localforage, LAST_CACHE_DATE_KEY, startedAt);
  }
  if (onProgress) onProgress(processedCount, total, 'completed');
  return {
    success: successCount,
    failed: failedCount,
    notFound: notFoundCount,
    skipped: skippedCount,
    processedCount,
    pending: pending.size,
    cancelled,
  };
};

let imageCacheRun = Promise.resolve();
export const storeProductImages = (...args) => {
  const run = imageCacheRun.then(() => runProductImageCache(...args));
  imageCacheRun = run.catch(() => {});
  return run;
};

/**
 * Counts the number of existing cached images from localForage only.
 * No API calls - only checks what's already cached.
 *
 * @return {Promise<number>} - The count of cached images.
 * */
export const getExistingCachedCount = async () => {
  try {
    const keys = await imageStore.keys();
    return keys.length;
  } catch (error) {
    console.error('Error counting cached images:', error);
    return 0;
  }
};

// MOBILE: Get current cache size and stats
export const getImageCacheStats = async () => {
  try {
    const keys = await imageStore.keys();
    let totalSize = 0;
    let imageCount = keys.length;

    // Sample some images to estimate total size
    const sampleKeys = keys.slice(0, Math.min(10, keys.length));
    let sampleSize = 0;

    for (const key of sampleKeys) {
      try {
        const blob = await imageStore.getItem(key);
        if (blob instanceof Blob) {
          sampleSize += blob.size;
        }
      } catch (e) {
        // Skip corrupted items
      }
    }

    // Estimate total size based on sample
    const avgImageSize = sampleSize / sampleKeys.length || 0;
    totalSize = Math.round((avgImageSize * imageCount) / (1024 * 1024)); // MB

    return { imageCount, totalSize };
  } catch (error) {
    console.error('Error getting cache stats:', error);
    return { imageCount: 0, totalSize: 0 };
  }
};

// MOBILE: Aggressive image cache cleanup for low-memory devices
export const cleanupImageCache = async (forceCleanup = false) => {
  try {
    const config = getCacheConfig();
    const stats = await getImageCacheStats();

    console.log(
      `Image cache stats: ${stats.imageCount} images, ~${stats.totalSize}MB`,
    );

    // Determine if cleanup is needed
    const needsCleanup =
      forceCleanup ||
      stats.totalSize > config.cleanupThreshold ||
      stats.imageCount > config.maxImages;

    if (!needsCleanup) {
      return { cleaned: false, reason: 'No cleanup needed' };
    }

    console.warn(
      `🧹 Starting aggressive image cache cleanup (${stats.totalSize}MB)`,
    );

    const keys = await imageStore.keys();
    const mappingKeys = await imageUrlMappingStore.keys();

    // Strategy 1: Remove only oldest 20% of images (much gentler)
    const percentToRemove = forceCleanup ? 0.4 : 0.2; // Force cleanup removes 40%, normal removes 20%
    const keysToRemove = keys.slice(
      0,
      Math.floor(keys.length * percentToRemove),
    );

    let removedCount = 0;
    for (const key of keysToRemove) {
      try {
        await imageStore.removeItem(key);
        removedCount++;
      } catch (e) {
        console.warn('Failed to remove image:', key);
      }
    }

    // Strategy 2: Clean up orphaned mappings
    let orphanedMappings = 0;
    for (const mappingKey of mappingKeys) {
      try {
        const imageUrl = await imageUrlMappingStore.getItem(mappingKey);
        const imageExists = await imageStore.getItem(imageUrl);
        if (!imageExists) {
          await imageUrlMappingStore.removeItem(mappingKey);
          orphanedMappings++;
        }
      } catch (e) {
        // Remove corrupted mappings
        await imageUrlMappingStore.removeItem(mappingKey);
        orphanedMappings++;
      }
    }

    // Strategy 3: Clean up some not-found cache entries during cleanup
    let cleanedNotFound = 0;
    if (forceCleanup) {
      try {
        await notFoundImagesStore.clear();
        cleanedNotFound = await notFoundImagesStore.length();
        console.log(`🗑️ Cleared all not-found image cache entries`);
      } catch (e) {
        console.warn('Failed to clear not-found cache:', e);
      }
    }

    const newStats = await getImageCacheStats();
    console.log(
      `✅ Cleanup complete: ${removedCount} images, ${orphanedMappings} mappings, ${cleanedNotFound} not-found entries removed`,
    );
    console.log(
      `📊 New stats: ${newStats.imageCount} images, ~${newStats.totalSize}MB`,
    );

    return {
      cleaned: true,
      removedImages: removedCount,
      removedMappings: orphanedMappings,
      beforeSize: stats.totalSize,
      afterSize: newStats.totalSize,
      savedMB: stats.totalSize - newStats.totalSize,
    };
  } catch (error) {
    console.error('Image cache cleanup failed:', error);
    return { cleaned: false, error: error.message };
  }
};

/**
 * Clears the not-found images cache to allow re-checking for newly added images
 * Should be called before cache refresh operations
 */
export const clearNotFoundCache = async () => {
  try {
    await withStorageTimeout(notFoundImagesStore.clear());
    console.log(
      '[Cache] Not-found images cache cleared - will re-check previously missing images',
    );
    return true;
  } catch (error) {
    console.error('[Cache] Error clearing not-found cache:', error);
    return false;
  }
};

export const clearNotFoundForProduct = async (productCode) => {
  try {
    await notFoundImagesStore.removeItem(productCode);
    return true;
  } catch (error) {
    return false;
  }
};

export const refetchProductImage = async (productCode, { signal } = {}) => {
  if (!productCode) return null;

  try {
    throwIfImageRequestAborted(signal);
    await notFoundImagesStore.removeItem(productCode);
    throwIfImageRequestAborted(signal);

    const imageUrl = await getImageUrlFromStrapi(productCode, { signal });
    throwIfImageRequestAborted(signal);
    if (!imageUrl) {
      await markImageMissing(productCode);
      return null;
    }

    // 'reload' bypasses the HTTP cache so an updated image replaces the stale one.
    const networkResponse = await fetch(imageUrl, { cache: 'reload', signal });
    if (!networkResponse.ok) {
      return null;
    }

    const imageBlob = await networkResponse.blob();
    throwIfImageRequestAborted(signal);
    await safeSetItem(imageStore, imageUrl, imageBlob);
    throwIfImageRequestAborted(signal);
    await safeSetItem(imageUrlMappingStore, productCode, imageUrl);
    throwIfImageRequestAborted(signal);
    return URL.createObjectURL(imageBlob);
  } catch (error) {
    if (signal?.aborted || error.name === 'AbortError') return null;
    console.error(`[Refetch] Failed for ${productCode}:`, error);
    return null;
  }
};

/**
 * Retrieves the last cache date from localForage.
 *
 * @return {Promise<string|null>} - The last cache date in ISO format or null if not found.
 * */
export const getLastCacheDate = async () => {
  try {
    const storedDate = await withStorageTimeout(localforage.getItem(LAST_CACHE_DATE_KEY));
    return typeof storedDate === 'string' && storedDate.length > 0
      ? storedDate
      : null;
  } catch (error) {
    console.error('[Cache Date] Error retrieving last cache date:', error);
    return null;
  }
};

/**
 * Clears the image store and removes the last cache date from localForage.
 *
 * @return {Promise<void>} - A promise that resolves when the operation is complete.
 * */
export const clearImageStore = async () => {
  try {
    await imageStore.clear();
    console.log('[LocalForage Cleanup] Image store cleared.');

    await imageUrlMappingStore.clear();
    console.log('[LocalForage Cleanup] Image URL mapping store cleared.');

    await notFoundImagesStore.clear();
    console.log('[LocalForage Cleanup] Not found images store cleared.');

    await localforage.removeItem(LAST_CACHE_DATE_KEY);
    await localforage.removeItem(PENDING_IMAGE_CACHE_KEY);
    console.log('[Cache Date] Last cache date removed.');
  } catch (error) {
    console.error(
      '[LocalForage Cleanup] Error clearing image store or date:',
      error,
    );
    throw error;
  }
};
